import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { concatMap, retryWhen } from 'rxjs/operators';

/**
 * Returns true for errors that are likely transient and worth retrying:
 * - Network errors (status 0, e.g. timeout / connection refused)
 * - Server errors (5xx)
 * - Non-HttpErrorResponse errors (e.g. unexpected runtime errors in streams)
 *
 * Deterministic client errors (4xx) are NOT retried.
 */
export function isTransientHttpError(error: any): boolean {
  if (error instanceof HttpErrorResponse) {
    return error.status === 0 || error.status >= 500;
  }
  // Non-HTTP errors (e.g. runtime exceptions) are assumed transient
  return true;
}

export interface RetryBackoffConfig {
  /** Maximum number of retry attempts. Default: 3 */
  maxRetries?: number;
  /** Initial delay in ms before the first retry. Default: 1000 */
  initialDelayMs?: number;
  /** Maximum delay in ms (caps the exponential growth). Default: 8000 */
  maxDelayMs?: number;
  /** Optional label used in console warning for easier debugging. */
  label?: string;
  /**
   * Optional predicate — only retry if this returns true for the error.
   * Default: `isTransientHttpError` (retries status 0 / 5xx only).
   */
  shouldRetry?: (error: any) => boolean;
}

const DEFAULTS: Required<Omit<RetryBackoffConfig, 'label' | 'shouldRetry'>> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 8000,
};

/**
 * RxJS pipeable operator that retries a failed observable with exponential backoff.
 *
 * @example
 * this.http.get(url).pipe(
 *   retryBeforeThrow({ maxRetries: 3, label: 'MyService.load' })
 * )
 */
export function retryBeforeThrow<T>(
  config: RetryBackoffConfig = {}
): (source: Observable<T>) => Observable<T> {
  const {
    maxRetries = DEFAULTS.maxRetries,
    initialDelayMs = DEFAULTS.initialDelayMs,
    maxDelayMs = DEFAULTS.maxDelayMs,
    label,
    shouldRetry = isTransientHttpError,
  } = config;

  return (source: Observable<T>) => {
    let attempt = 0;

    return source.pipe(
      retryWhen((errors) =>
        errors.pipe(
          concatMap((error) => {
            attempt++;

            if (attempt > maxRetries || !shouldRetry(error)) {
              console.log(
                'retry-before-throw.retries exhausted or not retryable, throwing error'
              );
              return throwError(error);
            }

            const delayMs = Math.min(
              initialDelayMs * Math.pow(2, attempt - 1),
              maxDelayMs
            );

            const tag = label ? `[${label}] ` : '';
            console.warn(
              `${tag}Retry ${attempt}/${maxRetries} after ${delayMs}ms`,
              error?.message || error
            );

            return timer(delayMs);
          })
        )
      )
    );
  };
}
